import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Image as ImageIcon, UploadSimple, CheckCircle, MusicNote, Play, Stop, Check, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [defaultTunes, setDefaultTunes] = useState([]);
  const [playingTune, setPlayingTune] = useState(null);
  const [selectingTune, setSelectingTune] = useState(false);
  const audioRef = useRef(null);
  
  const [formData, setFormData] = useState({
    support_phone: '',
    whatsapp_link: '',
    support_chat_link: '',
    customer_logo_url: '',
    web_link: '',
    app_name: '',
    privacy_policy_text: '',
    terms_conditions_text: '',
    support_24x7_link: '',
    customer_login_link: '',
    telegram_link: '',
    instagram_link: '',
    utr_block_mode: 'warn'
  });

  const [previews, setPreviews] = useState({
    appLogo: null,
    webpageLogo: null,
    customerLogo: null
  });

  useEffect(() => {
    loadSettings();
    loadDefaultTunes();
    
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const loadDefaultTunes = async () => {
    try {
      const response = await axios.get(`${API}/notification-tunes/default`);
      setDefaultTunes([...(response.data.tunes || []), { id: 'none', name: 'None (Silent)', preview_url: null }]);
    } catch (error) {
      console.error('Failed to load default tunes:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setFormData({
        support_phone: response.data.support_phone || '',
        whatsapp_link: response.data.whatsapp_link || '',
        support_chat_link: response.data.support_chat_link || '',
        customer_logo_url: response.data.customer_logo_url || '',
        web_link: response.data.web_link || '',
        app_name: response.data.app_name || 'DWS Vault Chat',
        privacy_policy_text: response.data.privacy_policy_text || '',
        terms_conditions_text: response.data.terms_conditions_text || '',
        support_24x7_link: response.data.support_24x7_link || '',
        customer_login_link: response.data.customer_login_link || '',
        telegram_link: response.data.telegram_link || '',
        instagram_link: response.data.instagram_link || '',
        utr_block_mode: response.data.utr_block_mode === 'block' ? 'block' : 'warn'
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (logoType === 'customer') {
        setPreviews(prev => ({ ...prev, customerLogo: event.target.result }));
      } else {
        setPreviews(prev => ({
          ...prev,
          [logoType === 'app' ? 'appLogo' : 'webpageLogo']: event.target.result
        }));
      }
    };
    reader.readAsDataURL(file);

    try {
      setUploading(true);
      
      if (logoType === 'customer') {
        // Upload customer logo via different endpoint
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await axios.post(`${API}/upload/customer-logo`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Construct full URL
        const fullLogoUrl = `${BACKEND_URL}${response.data.file_url}`;
        
        // Update settings with new logo URL
        await axios.put(`${API}/settings/update`, {
          customer_logo_url: fullLogoUrl
        });
        
        toast.success('Customer logo uploaded!');
        
        // Wait a bit for backend to save
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('logo_type', logoType);

        await axios.post(`${API}/upload/logo`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        toast.success(`${logoType === 'app' ? 'App' : 'Webpage'} logo uploaded!`);
      }
      
      // Reload settings to get updated URLs
      await loadSettings();
      setUploading(false);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setUploading(false);
    }
  };

  const handleNotificationTuneUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/upload/notification-tune`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Notification tune uploaded! Playing preview…');
      await loadSettings();
      // Auto-preview so admin immediately HEARS the tune they just set.
      try {
        const previewUrl = res?.data?.tune_url ? `${BACKEND_URL}${res.data.tune_url}` : null;
        if (previewUrl) {
          const a = new Audio(previewUrl);
          a.volume = 0.7;
          a.play().catch(() => {});
        }
      } catch (_) { /* ignore preview errors */ }
      setUploading(false);
    } catch (error) {
      console.error('Failed to upload tune:', error);
      toast.error('Failed to upload notification tune');
      setUploading(false);
    }
  };

  const handleUpdateSupport = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/settings/update`, formData);
      toast.success('Support links updated!');
      setLoading(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
      setLoading(false);
    }
  };

  const handlePlayTune = async (tuneUrl = null, tuneId = null) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingTune(null);
      
      // If clicking same tune, just stop
      if (tuneId && playingTune === tuneId) {
        return;
      }
    }
    
    // Determine which URL to play
    let audioUrl;
    if (tuneUrl) {
      // Playing a default tune preview - tuneUrl already has /api prefix
      audioUrl = `${BACKEND_URL}${tuneUrl}`;
    } else if (settings?.notification_tune_url) {
      // Playing current saved tune - need to add /api prefix if not present
      let tuneUrlPath = settings.notification_tune_url;
      // Ensure /api prefix is present
      if (tuneUrlPath.startsWith('/media/')) {
        tuneUrlPath = '/api' + tuneUrlPath;
      } else if (!tuneUrlPath.startsWith('/api/')) {
        tuneUrlPath = '/api/media/' + tuneUrlPath.split('/').pop();
      }
      audioUrl = `${BACKEND_URL}${tuneUrlPath}`;
    } else {
      toast.error('कोई notification tune सेट नहीं है');
      return;
    }
    
    console.log('Playing audio URL:', audioUrl);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      audio.volume = 0.7;
      
      // Set playing state
      if (tuneId) {
        setPlayingTune(tuneId);
      } else {
        setPlayingTune('current');
      }

      audio.addEventListener('ended', () => {
        setPlayingTune(null);
        audioRef.current = null;
      });

      audio.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        setPlayingTune(null);
        // If selected tune fails, reset to default
        if (!tuneUrl) {
          toast.error('Selected tune not found. Please re-select a tune.');
        } else {
          toast.error('Audio file not found.');
        }
      });

      // Set source and play
      audio.src = audioUrl;
      
      try {
        await audio.play();
        toast.success('🎵 Playing...');
      } catch (playError) {
        console.error('Play error:', playError);
        setPlayingTune(null);
        if (playError.name === 'NotAllowedError') {
          toast.error('Browser ने audio block किया। कृपया फिर से click करें।');
        } else {
          toast.error('Audio play में error आया।');
        }
      }
    } catch (error) {
      console.error('Failed to create audio:', error);
      setPlayingTune(null);
      toast.error('Audio load नहीं हो सका');
    }
  };

  const handleSelectDefaultTune = async (tuneId) => {
    try {
      setSelectingTune(true);
      
      const formData = new FormData();
      formData.append('tune_id', tuneId);
      
      const response = await axios.post(`${API}/notification-tunes/select`, formData);
      
      if (response.data.success) {
        toast.success(`${response.data.tune.name} सेट हो गया!`);
        await loadSettings();
      }
    } catch (error) {
      console.error('Failed to select tune:', error);
      toast.error('Tune select करने में error हुआ');
    } finally {
      setSelectingTune(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification Tune */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <MusicNote size={24} weight="bold" className="text-purple-600" />
          <h2 className="text-lg font-bold text-gray-800">Notification Tune</h2>
        </div>

        {/* Current Tune Display */}
        {settings?.notification_tune_url && (
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MusicNote size={24} weight="fill" className="text-purple-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {settings.notification_tune_name || 'Current Notification Tune'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {settings.notification_tune_type === 'default' ? 'Default tune' : 'Uploaded custom sound'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handlePlayTune()}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                  playingTune === 'current'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {playingTune === 'current' ? (
                  <>
                    <Stop size={18} weight="fill" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play size={18} weight="fill" />
                    Play Preview
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Default Tunes Selection */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Default Notification Tunes चुनें</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {defaultTunes.map((tune) => {
              const isSelected = settings?.notification_tune_id === tune.id;
              const isPlaying = playingTune === tune.id;
              
              return (
                <div
                  key={tune.id}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    isSelected 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <Check size={18} weight="bold" className="text-purple-600" />
                    )}
                    <span className={`text-sm font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {tune.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Preview Button */}
                    <button
                      onClick={() => handlePlayTune(tune.url, tune.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        isPlaying
                          ? 'bg-red-100 text-red-600 hover:bg-red-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={isPlaying ? 'Stop' : 'Preview'}
                    >
                      {isPlaying ? <Stop size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                    </button>
                    
                    {/* Select Button */}
                    {!isSelected && (
                      <button
                        onClick={() => handleSelectDefaultTune(tune.id)}
                        disabled={selectingTune}
                        className="px-3 py-1 text-xs font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        Select
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload Custom Sound */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">या अपनी Custom Tune Upload करें</h3>
          <label className="flex flex-col items-center px-4 py-6 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <MusicNote size={32} className="text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">Upload Custom Notification Sound</span>
            <span className="text-xs text-gray-500 mt-1">MP3, WAV, OGG, M4A, AAC formats supported</span>
            <input
              type="file"
              accept="audio/mpeg, audio/wav, audio/ogg, audio/mp3, audio/m4a, audio/aac, audio/x-m4a"
              onChange={handleNotificationTuneUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Call Ringtone Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Phone size={20} className="text-green-600" />
          Call Ringtone
        </h2>
        <p className="text-sm text-gray-500 mb-4">Incoming call ringtone for handlers and customers</p>
        
        <div className="space-y-2">
          {[
            { id: 'ring_classic', name: 'Classic Ring', desc: 'Standard phone ring' },
            { id: 'ring_gentle', name: 'Gentle Tone', desc: 'Soft melodic ring' },
            { id: 'ring_vibrant', name: 'Vibrant', desc: 'Energetic modern ring' },
            { id: 'ring_minimal', name: 'Minimal', desc: 'Simple clean beep' },
            { id: 'ring_professional', name: 'Professional', desc: 'Business-style ring' },
          ].map(ring => {
            const isSelected = (settings?.call_ringtone || 'ring_classic') === ring.id;
            return (
              <div key={ring.id} className={`flex items-center justify-between p-3 rounded-lg border ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">{ring.name}</p>
                  <p className="text-xs text-gray-500">{ring.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const freqs = { ring_classic: 440, ring_gentle: 330, ring_vibrant: 550, ring_minimal: 660, ring_professional: 392 };
                      const ctx = new (window.AudioContext || window.webkitAudioContext)();
                      const osc = ctx.createOscillator();
                      const gain = ctx.createGain();
                      osc.connect(gain); gain.connect(ctx.destination);
                      osc.frequency.value = freqs[ring.id] || 440;
                      osc.type = ring.id === 'ring_gentle' ? 'sine' : ring.id === 'ring_vibrant' ? 'square' : 'triangle';
                      gain.gain.value = 0.3;
                      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);
                      osc.start(); osc.stop(ctx.currentTime + 1);
                    }}
                    className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                  >
                    <Play size={14} />
                  </button>
                  {!isSelected && (
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/settings/update`, { call_ringtone: ring.id });
                          setSettings(prev => ({...prev, call_ringtone: ring.id}));
                          toast.success('Call ringtone updated!');
                        } catch(e) { toast.error('Failed to update'); }
                      }}
                      className="px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Select
                    </button>
                  )}
                  {isSelected && <span className="text-xs font-semibold text-green-600">Active</span>}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Custom Call Ringtone Upload */}
        <div className="mt-4">
          <label className="flex flex-col items-center px-4 py-4 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <MusicNote size={24} className="text-gray-400 mb-1" />
            <span className="text-sm text-gray-600">Upload Custom Call Ringtone</span>
            <span className="text-xs text-gray-500 mt-1">MP3, WAV, OGG formats</span>
            <input
              type="file"
              accept="audio/mpeg, audio/wav, audio/ogg, audio/mp3"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  const res = await axios.post(`${API}/upload/notification-tune`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  await axios.put(`${API}/settings/update`, { call_ringtone: 'custom', call_ringtone_url: res.data.url });
                  setSettings(prev => ({...prev, call_ringtone: 'custom', call_ringtone_url: res.data.url}));
                  toast.success('Custom ringtone uploaded!');
                } catch(e) { toast.error('Failed to upload'); }
              }}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Support Links */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Support Links & App Settings</h2>

        <div className="space-y-4">
          {/* App Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Name
            </label>
            <input
              type="text"
              name="app_name"
              value={formData.app_name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., DWS Vault Chat"
            />
            <p className="text-xs text-gray-500 mt-1">App name shown across the platform</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Phone Number
            </label>
            <input
              type="text"
              name="support_phone"
              value={formData.support_phone}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., +919876543210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WhatsApp Link
            </label>
            <input
              type="text"
              name="whatsapp_link"
              value={formData.whatsapp_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://wa.me/919876543210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telegram Link
            </label>
            <input
              type="text"
              name="telegram_link"
              value={formData.telegram_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://t.me/your_channel"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Instagram Link
            </label>
            <input
              type="text"
              name="instagram_link"
              value={formData.instagram_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://instagram.com/your_page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Chat Link
            </label>
            <input
              type="text"
              name="support_chat_link"
              value={formData.support_chat_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://support.example.com/chat"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Login Link
            </label>
            <input
              type="text"
              name="customer_login_link"
              value={formData.customer_login_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://your-app.com/customer-login"
            />
            <p className="text-xs text-gray-500 mt-1">This link is sent to customers when registered from chat</p>
          </div>

          {/* Privacy Policy Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Policy</label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b px-3 py-2 flex flex-wrap gap-1">
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('bold'); el.focus(); }} className="px-2 py-1 text-xs font-bold hover:bg-gray-200 rounded">B</button>
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('italic'); el.focus(); }} className="px-2 py-1 text-xs italic hover:bg-gray-200 rounded">I</button>
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('underline'); el.focus(); }} className="px-2 py-1 text-xs underline hover:bg-gray-200 rounded">U</button>
                <span className="border-l mx-1" />
                <select onChange={(e) => { document.execCommand('fontName', false, e.target.value); document.getElementById('privacy-editor').focus(); }} className="text-xs border rounded px-1 py-0.5">
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                </select>
                <select onChange={(e) => { document.execCommand('fontSize', false, e.target.value); document.getElementById('privacy-editor').focus(); }} className="text-xs border rounded px-1 py-0.5">
                  <option value="2">Small</option>
                  <option value="3" selected>Normal</option>
                  <option value="4">Large</option>
                  <option value="5">XL</option>
                </select>
                <span className="border-l mx-1" />
                <button type="button" onClick={() => { document.execCommand('insertUnorderedList'); document.getElementById('privacy-editor').focus(); }} className="px-2 py-1 text-xs hover:bg-gray-200 rounded">List</button>
              </div>
              <div
                id="privacy-editor"
                contentEditable
                className="min-h-[120px] px-4 py-3 text-sm focus:outline-none"
                dangerouslySetInnerHTML={{ __html: formData.privacy_policy_text }}
                onBlur={(e) => setFormData(prev => ({ ...prev, privacy_policy_text: e.target.innerHTML }))}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Shown on Customer Login page</p>
          </div>

          {/* UTR Duplicate Block Mode toggle */}
          <div className="border border-orange-200 bg-orange-50 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800">Duplicate UTR Mode</div>
                <div className="text-[11px] text-gray-600 mt-0.5">
                  {formData.utr_block_mode === 'block'
                    ? '🚫 HARD BLOCK — Handlers cannot mark-paid or deposit with a UTR already used elsewhere.'
                    : '⚠️ SOFT WARN — Handlers see a red banner but the entry still submits.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, utr_block_mode: prev.utr_block_mode === 'block' ? 'warn' : 'block' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0 transition ${formData.utr_block_mode === 'block' ? 'bg-red-600' : 'bg-gray-300'}`}
                data-testid="utr-block-mode-toggle"
                aria-pressed={formData.utr_block_mode === 'block'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${formData.utr_block_mode === 'block' ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          </div>

          {/* Terms & Conditions Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions</label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b px-3 py-2 flex flex-wrap gap-1">
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('bold'); el.focus(); }} className="px-2 py-1 text-xs font-bold hover:bg-gray-200 rounded">B</button>
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('italic'); el.focus(); }} className="px-2 py-1 text-xs italic hover:bg-gray-200 rounded">I</button>
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('underline'); el.focus(); }} className="px-2 py-1 text-xs underline hover:bg-gray-200 rounded">U</button>
                <span className="border-l mx-1" />
                <select onChange={(e) => { document.execCommand('fontName', false, e.target.value); document.getElementById('terms-editor').focus(); }} className="text-xs border rounded px-1 py-0.5">
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                </select>
                <select onChange={(e) => { document.execCommand('fontSize', false, e.target.value); document.getElementById('terms-editor').focus(); }} className="text-xs border rounded px-1 py-0.5">
                  <option value="2">Small</option>
                  <option value="3" selected>Normal</option>
                  <option value="4">Large</option>
                  <option value="5">XL</option>
                </select>
                <span className="border-l mx-1" />
                <button type="button" onClick={() => { document.execCommand('insertUnorderedList'); document.getElementById('terms-editor').focus(); }} className="px-2 py-1 text-xs hover:bg-gray-200 rounded">List</button>
              </div>
              <div
                id="terms-editor"
                contentEditable
                className="min-h-[120px] px-4 py-3 text-sm focus:outline-none"
                dangerouslySetInnerHTML={{ __html: formData.terms_conditions_text }}
                onBlur={(e) => setFormData(prev => ({ ...prev, terms_conditions_text: e.target.innerHTML }))}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Shown on Customer Login page</p>
          </div>

          <button
            onClick={handleUpdateSupport}
            disabled={loading}
            className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
          >
            <CheckCircle size={20} weight="bold" />
            {loading ? 'Updating...' : 'Update Support Links'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppSettingsManagement;
